import React, { useEffect, useRef, useState } from "react";
import {
  FaCalendar,
  FaHome,
  FaPlus,
  FaRupeeSign,
  FaSearch,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../css/pages/AddBooking.css";
import { useHiddenFields } from "../hooks/useHiddenFields";
import { usePageTitle } from "../hooks/usePageTitle";
import bookingService, {
  BookingApplicant,
  BookingPaymentPlan,
  CreateBookingData,
  Unit,
} from "../services/bookingService";
import { Broker, brokerService } from "../services/brokerService";
import { Project, projectService } from "../services/projectService";
import { formatDateForInput, getMaxTransactionDate } from "../utils/formatters";
import { formatMainDBAmount, formatSystemsDBAmount, formatTotalAmount } from "../utils/formatAmount";
import { filterUnitsBySearch, formatUnitDisplay } from "../utils/unitSearch";

const AddBooking: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const { hiddenFields } = useHiddenFields();

  const [projects, setProjects] = useState<Project[]>([]);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [availableUnits, setAvailableUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form states
  const [formData, setFormData] = useState<CreateBookingData>({
    projectId: 0,
    unitId: 0,
    bookingDate: formatDateForInput(new Date()),
    source: "",
    planType: "down_payment",
    saleDeedAmount: 0,
    chargesAmount: 0,
    otherWorkAmount: 0,
    terraceAmount: 0,
    banakhatAmount: 0,
    applicants: [
      {
        firstName: "",
        fatherHusbandName: "",
        surname: "",
        panCard: "",
        aadharCard: "",
        contactNumber: "",
        isPrimary: true,
      },
    ],
  });
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Unit search states
  const [unitSearchTerm, setUnitSearchTerm] = useState("");
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);

  // Payment plan states
  const [paymentPlans, setPaymentPlans] = useState<
    Omit<BookingPaymentPlan, "id" | "isActive">[]
  >([]);

  // Dropdown ref for click outside functionality
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Phone numbers state - array of {countryCode, number} per applicant
  const [applicantPhones, setApplicantPhones] = useState<{
    countryCode: string;
    number: string;
  }[][]>([[{ countryCode: '+91', number: '' }]]);

  // Country codes list - comprehensive list
  const countryCodes = [
    { code: '+91', country: 'India' },
    { code: '+93', country: 'Afghanistan' },
    { code: '+355', country: 'Albania' },
    { code: '+213', country: 'Algeria' },
    { code: '+376', country: 'Andorra' },
    { code: '+244', country: 'Angola' },
    { code: '+54', country: 'Argentina' },
    { code: '+374', country: 'Armenia' },
    { code: '+61', country: 'Australia' },
    { code: '+43', country: 'Austria' },
    { code: '+994', country: 'Azerbaijan' },
    { code: '+973', country: 'Bahrain' },
    { code: '+880', country: 'Bangladesh' },
    { code: '+375', country: 'Belarus' },
    { code: '+32', country: 'Belgium' },
    { code: '+501', country: 'Belize' },
    { code: '+229', country: 'Benin' },
    { code: '+975', country: 'Bhutan' },
    { code: '+591', country: 'Bolivia' },
    { code: '+387', country: 'Bosnia' },
    { code: '+267', country: 'Botswana' },
    { code: '+55', country: 'Brazil' },
    { code: '+673', country: 'Brunei' },
    { code: '+359', country: 'Bulgaria' },
    { code: '+226', country: 'Burkina Faso' },
    { code: '+257', country: 'Burundi' },
    { code: '+855', country: 'Cambodia' },
    { code: '+237', country: 'Cameroon' },
    { code: '+1', country: 'Canada' },
    { code: '+238', country: 'Cape Verde' },
    { code: '+236', country: 'Central African Republic' },
    { code: '+235', country: 'Chad' },
    { code: '+56', country: 'Chile' },
    { code: '+86', country: 'China' },
    { code: '+57', country: 'Colombia' },
    { code: '+269', country: 'Comoros' },
    { code: '+242', country: 'Congo' },
    { code: '+506', country: 'Costa Rica' },
    { code: '+385', country: 'Croatia' },
    { code: '+53', country: 'Cuba' },
    { code: '+357', country: 'Cyprus' },
    { code: '+420', country: 'Czech Republic' },
    { code: '+45', country: 'Denmark' },
    { code: '+253', country: 'Djibouti' },
    { code: '+593', country: 'Ecuador' },
    { code: '+20', country: 'Egypt' },
    { code: '+503', country: 'El Salvador' },
    { code: '+240', country: 'Equatorial Guinea' },
    { code: '+291', country: 'Eritrea' },
    { code: '+372', country: 'Estonia' },
    { code: '+251', country: 'Ethiopia' },
    { code: '+679', country: 'Fiji' },
    { code: '+358', country: 'Finland' },
    { code: '+33', country: 'France' },
    { code: '+241', country: 'Gabon' },
    { code: '+220', country: 'Gambia' },
    { code: '+995', country: 'Georgia' },
    { code: '+49', country: 'Germany' },
    { code: '+233', country: 'Ghana' },
    { code: '+30', country: 'Greece' },
    { code: '+502', country: 'Guatemala' },
    { code: '+224', country: 'Guinea' },
    { code: '+245', country: 'Guinea-Bissau' },
    { code: '+592', country: 'Guyana' },
    { code: '+509', country: 'Haiti' },
    { code: '+504', country: 'Honduras' },
    { code: '+852', country: 'Hong Kong' },
    { code: '+36', country: 'Hungary' },
    { code: '+354', country: 'Iceland' },
    { code: '+91', country: 'India' },
    { code: '+62', country: 'Indonesia' },
    { code: '+98', country: 'Iran' },
    { code: '+964', country: 'Iraq' },
    { code: '+353', country: 'Ireland' },
    { code: '+972', country: 'Israel' },
    { code: '+39', country: 'Italy' },
    { code: '+225', country: 'Ivory Coast' },
    { code: '+81', country: 'Japan' },
    { code: '+962', country: 'Jordan' },
    { code: '+7', country: 'Kazakhstan' },
    { code: '+254', country: 'Kenya' },
    { code: '+965', country: 'Kuwait' },
    { code: '+996', country: 'Kyrgyzstan' },
    { code: '+856', country: 'Laos' },
    { code: '+371', country: 'Latvia' },
    { code: '+961', country: 'Lebanon' },
    { code: '+266', country: 'Lesotho' },
    { code: '+231', country: 'Liberia' },
    { code: '+218', country: 'Libya' },
    { code: '+423', country: 'Liechtenstein' },
    { code: '+370', country: 'Lithuania' },
    { code: '+352', country: 'Luxembourg' },
    { code: '+853', country: 'Macau' },
    { code: '+261', country: 'Madagascar' },
    { code: '+265', country: 'Malawi' },
    { code: '+60', country: 'Malaysia' },
    { code: '+960', country: 'Maldives' },
    { code: '+223', country: 'Mali' },
    { code: '+356', country: 'Malta' },
    { code: '+222', country: 'Mauritania' },
    { code: '+230', country: 'Mauritius' },
    { code: '+52', country: 'Mexico' },
    { code: '+373', country: 'Moldova' },
    { code: '+377', country: 'Monaco' },
    { code: '+976', country: 'Mongolia' },
    { code: '+382', country: 'Montenegro' },
    { code: '+212', country: 'Morocco' },
    { code: '+258', country: 'Mozambique' },
    { code: '+95', country: 'Myanmar' },
    { code: '+264', country: 'Namibia' },
    { code: '+977', country: 'Nepal' },
    { code: '+31', country: 'Netherlands' },
    { code: '+64', country: 'New Zealand' },
    { code: '+505', country: 'Nicaragua' },
    { code: '+227', country: 'Niger' },
    { code: '+234', country: 'Nigeria' },
    { code: '+47', country: 'Norway' },
    { code: '+968', country: 'Oman' },
    { code: '+92', country: 'Pakistan' },
    { code: '+970', country: 'Palestine' },
    { code: '+507', country: 'Panama' },
    { code: '+675', country: 'Papua New Guinea' },
    { code: '+595', country: 'Paraguay' },
    { code: '+51', country: 'Peru' },
    { code: '+63', country: 'Philippines' },
    { code: '+48', country: 'Poland' },
    { code: '+351', country: 'Portugal' },
    { code: '+974', country: 'Qatar' },
    { code: '+40', country: 'Romania' },
    { code: '+7', country: 'Russia' },
    { code: '+250', country: 'Rwanda' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+221', country: 'Senegal' },
    { code: '+381', country: 'Serbia' },
    { code: '+248', country: 'Seychelles' },
    { code: '+232', country: 'Sierra Leone' },
    { code: '+65', country: 'Singapore' },
    { code: '+421', country: 'Slovakia' },
    { code: '+386', country: 'Slovenia' },
    { code: '+252', country: 'Somalia' },
    { code: '+27', country: 'South Africa' },
    { code: '+82', country: 'South Korea' },
    { code: '+211', country: 'South Sudan' },
    { code: '+34', country: 'Spain' },
    { code: '+94', country: 'Sri Lanka' },
    { code: '+249', country: 'Sudan' },
    { code: '+597', country: 'Suriname' },
    { code: '+268', country: 'Swaziland' },
    { code: '+46', country: 'Sweden' },
    { code: '+41', country: 'Switzerland' },
    { code: '+963', country: 'Syria' },
    { code: '+886', country: 'Taiwan' },
    { code: '+992', country: 'Tajikistan' },
    { code: '+255', country: 'Tanzania' },
    { code: '+66', country: 'Thailand' },
    { code: '+228', country: 'Togo' },
    { code: '+216', country: 'Tunisia' },
    { code: '+90', country: 'Turkey' },
    { code: '+993', country: 'Turkmenistan' },
    { code: '+256', country: 'Uganda' },
    { code: '+380', country: 'Ukraine' },
    { code: '+971', country: 'UAE' },
    { code: '+44', country: 'UK' },
    { code: '+1', country: 'USA' },
    { code: '+598', country: 'Uruguay' },
    { code: '+998', country: 'Uzbekistan' },
    { code: '+58', country: 'Venezuela' },
    { code: '+84', country: 'Vietnam' },
    { code: '+967', country: 'Yemen' },
    { code: '+260', country: 'Zambia' },
    { code: '+263', country: 'Zimbabwe' },
  ];

  // Source options
  const sourceOptions = [
    "Family/Friends",
    "Walk-in",
    "Hoarding Ads",
    "Newspaper Ads",
    "Pamphlet Ads",
    "Facebook",
    "Instagram",
    "Website",
    "Other",
  ];

  useEffect(() => {
    loadInitialData();
    // Initialize phone arrays from existing applicants
    const initialPhones = formData.applicants.map(app => 
      unmergePhoneNumbers(app.contactNumber)
    );
    setApplicantPhones(initialPhones);
  }, []);

  useEffect(() => {
    if (formData.projectId) {
      loadAvailableUnits(formData.projectId);
      loadBrokersForProject(formData.projectId);
    } else {
      setAvailableUnits([]);
      setSelectedUnit(null);
      setBrokers([]);
    }
  }, [formData.projectId]);

  const loadBrokersForProject = async (projectId: number) => {
    try {
      const brokersData = await brokerService.getBrokers({ 
        page: 1, 
        limit: 1000,
        projectId: projectId 
      });
      setBrokers(brokersData.brokers);
    } catch (err) {
      console.error("Failed to load brokers for project:", err);
    }
  };

  useEffect(() => {
    if (formData.unitId && availableUnits.length > 0) {
      const unit = availableUnits.find((u) => u.id === formData.unitId);
      setSelectedUnit(unit || null);
      if (unit) {
        setFormData((prev) => ({
          ...prev,
          saleDeedAmount: unit.saleDeedAmount || 0,
          terraceAmount:
            unit.terraceStatus?.toLowerCase() === "yes"
              ? unit.extraSpaceAmount || 0
              : 0,
        }));
      }
    }
  }, [formData.unitId, availableUnits]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowUnitDropdown(false);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const loadInitialData = async () => {
    try {
      const [projectsData, brokersData] = await Promise.all([
        projectService.getProjects(),
        brokerService.getBrokers({ page: 1, limit: 100 }),
      ]);
      setProjects(projectsData);
      setBrokers(brokersData.brokers);
    } catch (err) {
      setError("Failed to load initial data");
    }
  };

  const loadAvailableUnits = async (projectId: number) => {
    try {
      const units = await bookingService.getUnsoldUnits(projectId);
      setAvailableUnits(units);
    } catch (err) {
      console.error("Failed to load available units:", err);
    }
  };

  // Utility: Merge phone numbers into single string for storage
  const mergePhoneNumbers = (phones: { countryCode: string; number: string }[]): string => {
    return phones
      .filter(p => p.number.trim())
      .map(p => `${p.countryCode}-${p.number.trim()}`)
      .join(',');
  };

  // Utility: Unmerge stored phone string into array
  const unmergePhoneNumbers = (phoneString: string): { countryCode: string; number: string }[] => {
    if (!phoneString || !phoneString.trim()) {
      return [{ countryCode: '+91', number: '' }];
    }
    
    // Handle both formats:
    // Old format: "9377727251 / 9023122672" (slash or comma separated, no country codes)
    // New format: "+91-9377727251,+91-9023122672" (comma separated with country codes)
    const parts = phoneString.split(/[,\/]/).map(p => p.trim()).filter(p => p);
    
    const phones = parts.map(part => {
      // Check if part contains country code (starts with + or has dash)
      if (part.includes('-')) {
        const [code, ...numParts] = part.split('-');
        return {
          countryCode: code.startsWith('+') ? code : '+91',
          number: numParts.join('-') // Handle numbers with dashes
        };
      }
      // Old format: just the number without country code
      return { countryCode: '+91', number: part };
    });
    
    return phones.length > 0 ? phones : [{ countryCode: '+91', number: '' }];
  };

  // Filter units based on search term - STANDARDIZED SEARCH
  const filteredUnits = availableUnits.filter((unit) => {
    // Use standardized unit search for exact "B-101" or "101" matching
    const unitMatches = filterUnitsBySearch([unit], unitSearchTerm).length > 0;

    // Also include additional filtering for unit type and size
    const typeMatches = unit.unitType
      .toLowerCase()
      .includes(unitSearchTerm.toLowerCase());
    const sizeMatches = unit.size
      .toLowerCase()
      .includes(unitSearchTerm.toLowerCase());

    return unitMatches || typeMatches || sizeMatches;
  });

  const handleUnitSelect = (unit: Unit) => {
    setFormData((prev) => ({ ...prev, unitId: unit.id }));
    setSelectedUnit(unit);
    setUnitSearchTerm(formatUnitDisplay(unit.wing, unit.unitNo));
    setShowUnitDropdown(false);

    // Auto-populate unit-related amounts
    setFormData((prev) => ({
      ...prev,
      saleDeedAmount: unit.saleDeedAmount || 0,
      terraceAmount:
        unit.terraceStatus?.toLowerCase() === "yes"
          ? unit.extraSpaceAmount || 0
          : 0,
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.projectId) errors.projectId = "Project is required";
    if (!formData.unitId) errors.unitId = "Unit is required";
    if (!formData.bookingDate) errors.bookingDate = "Booking date is required";
    if (!formData.source) errors.source = "Source is required";
    if (!formData.planType) errors.planType = "Plan type is required";

    // Sale deed and other work amount are mandatory
    if (!formData.saleDeedAmount || formData.saleDeedAmount <= 0) {
      errors.saleDeedAmount = "Sale deed amount is required";
    }
    if (
      formData.otherWorkAmount === undefined ||
      formData.otherWorkAmount === null
    ) {
      errors.otherWorkAmount = "Other work amount is required";
    }
    // Charges amount, terrace amount, and banakhat amount are optional, but must be >= 0 if provided
    if (formData.chargesAmount !== undefined && formData.chargesAmount < 0) {
      errors.chargesAmount = "Charges amount must be 0 or greater";
    }
    if (formData.terraceAmount !== undefined && formData.terraceAmount < 0) {
      errors.terraceAmount = "Terrace amount must be 0 or greater";
    }
    if (formData.banakhatAmount !== undefined && formData.banakhatAmount < 0) {
      errors.banakhatAmount = "Banakhat amount must be 0 or greater";
    }

    // Validate applicants
    formData.applicants.forEach((applicant, index) => {
      if (!applicant.firstName.trim()) {
        errors[`applicant_${index}_firstName`] = "First name is required";
      }
      if (!applicant.surname.trim()) {
        errors[`applicant_${index}_surname`] = "Surname is required";
      }
      if (!applicant.contactNumber.trim()) {
        errors[`applicant_${index}_contactNumber`] =
          "Contact number is required";
      } else {
        // Validate phone number format and length
        const phones = unmergePhoneNumbers(applicant.contactNumber);
        const invalidPhone = phones.find(p => {
          if (!p.number) return false;
          if (p.countryCode === '+91') return p.number.length !== 10;
          return p.number.length < 4;
        });
        if (invalidPhone) {
          errors[`applicant_${index}_contactNumber`] = invalidPhone.countryCode === '+91'
            ? "Indian phone number must be exactly 10 digits"
            : "Phone number must be at least 4 digits";
        }
      }
    });

    // Check if at least one primary applicant
    const primaryApplicants = formData.applicants.filter((a) => a.isPrimary);
    if (primaryApplicants.length === 0) {
      errors.primaryApplicant =
        "At least one applicant must be marked as primary";
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors);
      toast.error(errorMessages.join('\n'), { style: { whiteSpace: 'pre-line' } });
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const bookingData = {
        ...formData,
        paymentTerms: formData.planType, // Map planType to paymentTerms for backend
        paymentPlans: paymentPlans.length > 0 ? paymentPlans : undefined,
      };
      const booking = await bookingService.createBooking(bookingData);
      setSuccess(
        `Booking created successfully! Booking ID: ${booking.bookingId}`
      );

      // Redirect to bookings list after 2 seconds
      setTimeout(() => {
        navigate("/bookings");
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to create booking");
      toast.error(err.response?.data?.message || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  const addApplicant = () => {
    setFormData((prev) => ({
      ...prev,
      applicants: [
        ...prev.applicants,
        {
          firstName: "",
          fatherHusbandName: "",
          surname: "",
          panCard: "",
          aadharCard: "",
          contactNumber: "",
          isPrimary: false,
        },
      ],
    }));
    // Add phone array for new applicant
    setApplicantPhones(prev => [...prev, [{ countryCode: '+91', number: '' }]]);
  };

  const removeApplicant = (index: number) => {
    if (formData.applicants.length > 1) {
      setFormData((prev) => ({
        ...prev,
        applicants: prev.applicants.filter((_, i) => i !== index),
      }));
      // Remove phone array for removed applicant
      setApplicantPhones(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateApplicant = (
    index: number,
    field: keyof BookingApplicant,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      applicants: prev.applicants.map((applicant, i) => {
        if (i === index) {
          const updated = { ...applicant, [field]: value };
          // If updating any phone-related field, merge the phone numbers
          if (field === 'contactNumber') {
            updated.contactNumber = mergePhoneNumbers(applicantPhones[index] || [{ countryCode: '+91', number: '' }]);
          }
          return updated;
        }
        return applicant;
      }),
    }));
  };

  // Update phone number for specific applicant and phone index
  const updateApplicantPhone = (applicantIndex: number, phoneIndex: number, field: 'countryCode' | 'number', value: string) => {
    setApplicantPhones(prev => {
      const updated = [...prev];
      if (!updated[applicantIndex]) {
        updated[applicantIndex] = [{ countryCode: '+91', number: '' }];
      }
      updated[applicantIndex] = [...updated[applicantIndex]];
      updated[applicantIndex][phoneIndex] = {
        ...updated[applicantIndex][phoneIndex],
        [field]: value
      };
      
      // Update the merged contactNumber in formData
      setFormData(prevForm => ({
        ...prevForm,
        applicants: prevForm.applicants.map((app, i) => 
          i === applicantIndex 
            ? { ...app, contactNumber: mergePhoneNumbers(updated[applicantIndex]) }
            : app
        )
      }));
      
      return updated;
    });
  };

  // Add another phone number for applicant (max 2)
  const addPhoneNumber = (applicantIndex: number) => {
    setApplicantPhones(prev => {
      const updated = [...prev];
      if (updated[applicantIndex].length < 2) {
        updated[applicantIndex] = [...updated[applicantIndex], { countryCode: '+91', number: '' }];
      }
      return updated;
    });
  };

  // Remove phone number for applicant
  const removePhoneNumber = (applicantIndex: number, phoneIndex: number) => {
    setApplicantPhones(prev => {
      const updated = [...prev];
      updated[applicantIndex] = updated[applicantIndex].filter((_, i) => i !== phoneIndex);
      
      // Ensure at least one phone field remains
      if (updated[applicantIndex].length === 0) {
        updated[applicantIndex] = [{ countryCode: '+91', number: '' }];
      }
      
      // Update merged contactNumber
      setFormData(prevForm => ({
        ...prevForm,
        applicants: prevForm.applicants.map((app, i) => 
          i === applicantIndex 
            ? { ...app, contactNumber: mergePhoneNumbers(updated[applicantIndex]) }
            : app
        )
      }));
      
      return updated;
    });
  };

  // Payment Plan Functions
  const addPaymentPlan = () => {
    setPaymentPlans((prev) => [
      ...prev,
      {
        planType: "down_payment",
        amount: 0,
        description: "",
      },
    ]);
  };

  const removePaymentPlan = (index: number) => {
    setPaymentPlans((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePaymentPlan = (
    index: number,
    field: keyof BookingPaymentPlan,
    value: any
  ) => {
    setPaymentPlans((prev) =>
      prev.map((plan, i) => (i === index ? { ...plan, [field]: value } : plan))
    );
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ padding: '12px 20px', minHeight: 'auto' }}>
        <h2 style={{ color: "#1a1a1a", margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Add New Booking</h2>
        <button className="btn-secondary" onClick={() => navigate("/bookings")}>
          Back to Bookings
        </button>
      </div>

      {error && (
        <div style={{ display: 'none' }}>
          {error}
        </div>
      )}

      {success && <div className="success-message">{success}</div>}

      <div className="booking-form-container">
        <form onSubmit={handleSubmit} className="booking-form">
          {/* Booking Details Section */}
          <div className="form-section compact-section">
            <h3>
              <FaCalendar /> Booking Details
            </h3>

            {/* Row 1: Project and Unit */}
            <div className="form-row compact-row">
              <div className="form-group">
                <label>Project *</label>
                <select
                  value={formData.projectId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      projectId: parseInt(e.target.value),
                    }))
                  }
                  disabled={loading}
                >
                  <option value="">Select Project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {formErrors.projectId && (
                  <span className="error">{formErrors.projectId}</span>
                )}
              </div>

              <div className="form-group">
                <label>Unit *</label>
                <div className="searchable-dropdown" ref={dropdownRef}>
                  <div className="search-input-container">
                    <FaSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search units... (e.g., A-101)"
                      value={unitSearchTerm}
                      onChange={(e) => {
                        setUnitSearchTerm(e.target.value);
                        setShowUnitDropdown(true);
                      }}
                      onFocus={() => setShowUnitDropdown(true)}
                      disabled={!formData.projectId || loading}
                      className="form-input"
                    />
                  </div>
                  {showUnitDropdown && filteredUnits.length > 0 && (
                    <div className="dropdown-options">
                      {filteredUnits.map((unit) => (
                        <div
                          key={unit.id}
                          className="dropdown-option"
                          onClick={() => handleUnitSelect(unit)}
                        >
                          <div className="unit-option">
                            <div className="unit-number">
                              {formatUnitDisplay(unit.wing, unit.unitNo)}
                            </div>
                            <div className="unit-details">
                              {unit.unitType} • {unit.size} •{" "}
                              {formatMainDBAmount(unit.saleDeedAmount || 0)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formErrors.unitId && (
                  <span className="error">{formErrors.unitId}</span>
                )}
              </div>
            </div>

            {/* Row 2: Booking Date, Source, and Broker */}
            <div className="form-row compact-row">
              <div className="form-group">
                <label>Booking Date *</label>
                <input
                  type="date"
                  value={formData.bookingDate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      bookingDate: e.target.value,
                    }))
                  }
                  max={getMaxTransactionDate()}
                  disabled={loading}
                />
                {formErrors.bookingDate && (
                  <span className="error">{formErrors.bookingDate}</span>
                )}
              </div>

              <div className="form-group">
                <label>Source *</label>
                <select
                  value={formData.source}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, source: e.target.value }))
                  }
                  disabled={loading}
                >
                  <option value="">Select Source</option>
                  {sourceOptions.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
                {formErrors.source && (
                  <span className="error">{formErrors.source}</span>
                )}
              </div>

              <div className="form-group">
                <label>Broker (Optional)</label>
                <select
                  value={formData.brokerId || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      brokerId: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    }))
                  }
                  disabled={loading}
                >
                  <option value="">Select Broker</option>
                  {brokers.map((broker) => (
                    <option key={broker.id} value={broker.id}>
                      {broker.name} - {broker.mobile}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Unit Details (Read-only) */}
          {selectedUnit && (
            <div className="form-section unit-details">
              <h3>
                <FaHome /> Unit Details
              </h3>
              <div className="unit-info-grid">
                <div className="info-item">
                  <label>Unit Number:</label>
                  <span>
                    {selectedUnit.wing}-{selectedUnit.unitNo}
                  </span>
                </div>
                <div className="info-item">
                  <label>Unit Type:</label>
                  <span>{selectedUnit.unitType}</span>
                </div>
                <div className="info-item">
                  <label>Size:</label>
                  <span>{selectedUnit.size}</span>
                </div>
                <div className="info-item">
                  <label>Wing:</label>
                  <span>{selectedUnit.wing}</span>
                </div>
                <div className="info-item">
                  <label>Sale Deed Amount:</label>
                  <span className="amount">
                    {formatMainDBAmount(selectedUnit.saleDeedAmount || 0)}
                  </span>
                </div>
                <div className="info-item">
                  <label>Terrace:</label>
                  <span>{selectedUnit.terraceStatus || "No"}</span>
                </div>
                {selectedUnit.terraceStatus?.toLowerCase() === "yes" && (
                  <>
                    <div className="info-item">
                      <label>Terrace Size:</label>
                      <span>{selectedUnit.terraceSize}</span>
                    </div>
                    <div className="info-item">
                      <label>Terrace Amount:</label>
                      <span className="amount">
                        {formatSystemsDBAmount(selectedUnit.extraSpaceAmount || 0)}
                      </span>
                    </div>
                  </>
                )}
                {selectedUnit.extraSpace && (
                  <div className="info-item">
                    <label>Extra Space:</label>
                    <span>{selectedUnit.extraSpace}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Customer Details */}
          <div className="form-section compact-section">
            <div className="section-header">
              <h3>
                <FaUser /> Customer Details
              </h3>
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={addApplicant}
                disabled={loading}
              >
                <FaPlus /> Add Applicant
              </button>
            </div>

            {formData.applicants.map((applicant, index) => (
              <div key={index} className="applicant-section compact-applicant">
                <div className="applicant-header">
                  <h4>Applicant {index + 1}</h4>
                  {formData.applicants.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-danger btn-small"
                      onClick={() => removeApplicant(index)}
                      disabled={loading}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                {/* Row 1: First Name, Father/Husband, Surname */}
                <div className="form-row compact-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={applicant.firstName}
                      onChange={(e) =>
                        updateApplicant(index, "firstName", e.target.value)
                      }
                      disabled={loading}
                    />
                    {formErrors[`applicant_${index}_firstName`] && (
                      <span className="error">
                        {formErrors[`applicant_${index}_firstName`]}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Father/Husband Name</label>
                    <input
                      type="text"
                      value={applicant.fatherHusbandName}
                      onChange={(e) =>
                        updateApplicant(
                          index,
                          "fatherHusbandName",
                          e.target.value
                        )
                      }
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Surname *</label>
                    <input
                      type="text"
                      value={applicant.surname}
                      onChange={(e) =>
                        updateApplicant(index, "surname", e.target.value)
                      }
                      disabled={loading}
                    />
                    {formErrors[`applicant_${index}_surname`] && (
                      <span className="error">
                        {formErrors[`applicant_${index}_surname`]}
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 2: PAN, Aadhar, Contact Number */}
                <div className="form-row compact-row">
                  <div className="form-group">
                    <label>PAN Number</label>
                    <input
                      type="text"
                      value={applicant.panCard || ""}
                      onChange={(e) =>
                        updateApplicant(index, "panCard", e.target.value.toUpperCase())
                      }
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Aadhar Number</label>
                    <input
                      type="text"
                      value={applicant.aadharCard || ""}
                      onChange={(e) =>
                        updateApplicant(index, "aadharCard", e.target.value.replace(/\D/g, ''))
                      }
                      placeholder="1234 5678 9012"
                      maxLength={12}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Row 3: Phone Numbers with Country Code */}
                <div className="phone-numbers-section">
                  <label className="section-label">Contact Numbers *</label>
                  {(applicantPhones[index] || [{ countryCode: '+91', number: '' }]).map((phone, phoneIndex) => (
                    <div key={phoneIndex} className="phone-input-group">
                      <select
                        value={phone.countryCode}
                        onChange={(e) =>
                          updateApplicantPhone(index, phoneIndex, 'countryCode', e.target.value)
                        }
                        disabled={loading}
                        className="country-code-select"
                      >
                        {countryCodes.map(cc => (
                          <option key={cc.code} value={cc.code}>
                            {cc.code} ({cc.country})
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={phone.number}
                        onChange={(e) =>
                          updateApplicantPhone(index, phoneIndex, 'number', e.target.value.replace(/\D/g, ''))
                        }
                        placeholder="Enter phone number"
                        maxLength={phone.countryCode === '+91' ? 10 : 15}
                        disabled={loading}
                        className="phone-number-input"
                      />
                      {phoneIndex === 0 && (applicantPhones[index]?.length || 1) < 2 && (
                        <button
                          type="button"
                          className="btn-icon add-phone"
                          onClick={() => addPhoneNumber(index)}
                          disabled={loading}
                          title="Add another number"
                        >
                          <FaPlus />
                        </button>
                      )}
                      {phoneIndex > 0 && (
                        <button
                          type="button"
                          className="btn-icon remove-phone"
                          onClick={() => removePhoneNumber(index, phoneIndex)}
                          disabled={loading}
                          title="Remove this number"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  ))}
                  {formErrors[`applicant_${index}_contactNumber`] && (
                    <span className="error">
                      {formErrors[`applicant_${index}_contactNumber`]}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={applicant.isPrimary}
                      onChange={(e) => {
                        // If setting this as primary, unset all others
                        if (e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            applicants: prev.applicants.map((app, i) => ({
                              ...app,
                              isPrimary: i === index,
                            })),
                          }));
                        } else {
                          updateApplicant(index, "isPrimary", false);
                        }
                      }}
                      disabled={loading}
                    />
                    Primary Applicant
                  </label>
                </div>
              </div>
            ))}

            {formErrors.primaryApplicant && (
              <span className="error">{formErrors.primaryApplicant}</span>
            )}

            {/* Customer Address - Full Width */}
            <div className="form-group">
              <label>Customer Address</label>
              <input
                type="text"
                value={formData.customerAddress || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    customerAddress: e.target.value,
                  }))
                }
                disabled={loading}
                placeholder="Enter customer address"
              />
            </div>

            {/* Remarks - Full Width */}
            {!hiddenFields.booking.remarks && (
            <div className="form-group">
              <label>Remarks</label>
              <input
                type="text"
                value={formData.remarks || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, remarks: e.target.value }))
                }
                disabled={loading}
                placeholder="Enter remarks"
              />
            </div>
            )}
          </div>

          {/* Payment Terms */}
          <div className="form-section compact-section">
            <h3>
              <FaRupeeSign /> Payment Terms
            </h3>

            {/* Payment Terms Selection */}
            <div className="form-group">
              <label>Payment Terms *</label>
              <div className="radio-group" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="planType"
                    value="down_payment"
                    checked={formData.planType === "down_payment"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        planType: e.target.value as any,
                      }))
                    }
                  />
                  Down Payment
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="planType"
                    value="loan"
                    checked={formData.planType === "loan"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        planType: e.target.value as any,
                      }))
                    }
                  />
                  Loan
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="planType"
                    value="installments"
                    checked={formData.planType === "installments"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        planType: e.target.value as any,
                      }))
                    }
                  />
                  Installments
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="planType"
                    value="mixed_payment"
                    checked={formData.planType === "mixed_payment"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        planType: e.target.value as any,
                      }))
                    }
                  />
                  Mixed Payment
                </label>
              </div>
            </div>

            {/* All 5 Amounts in a Single Row */}
            <div className="form-row compact-row amounts-row">
              <div className="form-group">
                <label>Sale Deed Amount *</label>
                <input
                  type="number"
                  step="1"
                  value={
                    formData.saleDeedAmount === 0 ? "" : formData.saleDeedAmount
                  }
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      saleDeedAmount: parseFloat(e.target.value) || 0,
                    }))
                  }
                  onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                  min={selectedUnit?.saleDeedAmount || 0}
                  disabled={loading}
                  placeholder="Enter sale deed amount (no decimals)"
                  required
                />
                {formErrors.saleDeedAmount && (
                  <span className="error">{formErrors.saleDeedAmount}</span>
                )}
                {selectedUnit?.saleDeedAmount && (
                  <small>
                    Min: {formatMainDBAmount(selectedUnit.saleDeedAmount)}
                  </small>
                )}
              </div>

              {!hiddenFields.booking.otherWorkAmount && (
                <div className="form-group">
                  <label>Other Work Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={
                      formData.otherWorkAmount === 0
                        ? ""
                        : formData.otherWorkAmount
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        otherWorkAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    min="0"
                    disabled={loading}
                    placeholder="Enter other work amount"
                    required
                  />
                  {formErrors.otherWorkAmount && (
                    <span className="error">{formErrors.otherWorkAmount}</span>
                  )}
                </div>
              )}

              {!hiddenFields.booking.terraceAmount && (
                <div className="form-group">
                  <label>Terrace Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={
                      formData.terraceAmount === 0 ? "" : formData.terraceAmount
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        terraceAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    min="0"
                    disabled={
                      selectedUnit?.terraceStatus?.toLowerCase() !== "yes" ||
                      loading
                    }
                    placeholder="Enter terrace amount"
                  />
                  {formErrors.terraceAmount && (
                    <span className="error">{formErrors.terraceAmount}</span>
                  )}
                </div>
              )}

              {!hiddenFields.booking.chargesAmount && (
                <div className="form-group">
                  <label>Extra Work Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={
                      formData.chargesAmount === 0 ? "" : formData.chargesAmount
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        chargesAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    min="0"
                    disabled={loading}
                    placeholder="Enter extra work amount"
                  />
                  {formErrors.chargesAmount && (
                    <span className="error">{formErrors.chargesAmount}</span>
                  )}
                </div>
              )}

              {!hiddenFields.booking.banakhatAmount && (
                <div className="form-group">
                  <label>Banakhat Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={
                      formData.banakhatAmount === 0
                        ? ""
                        : formData.banakhatAmount
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        banakhatAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    min="0"
                    disabled={loading}
                    placeholder="Enter banakhat amount"
                  />
                  {formErrors.banakhatAmount && (
                    <span className="error">{formErrors.banakhatAmount}</span>
                  )}
                </div>
              )}
            </div>

            <div className="total-amount">
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#059669",
                  }}
                >
                  Main: {formatMainDBAmount(formData.saleDeedAmount || 0)}
                </div>
                {(!hiddenFields.booking.chargesAmount ||
                  !hiddenFields.booking.otherWorkAmount ||
                  !hiddenFields.booking.terraceAmount ||
                  !hiddenFields.booking.banakhatAmount) && (
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    Receivables:{" "}
                    {formatSystemsDBAmount(
                      (!hiddenFields.booking.chargesAmount
                        ? formData.chargesAmount || 0
                        : 0) +
                        (!hiddenFields.booking.otherWorkAmount
                          ? formData.otherWorkAmount || 0
                          : 0) +
                        (!hiddenFields.booking.terraceAmount
                          ? formData.terraceAmount || 0
                          : 0) +
                        (!hiddenFields.booking.banakhatAmount
                          ? formData.banakhatAmount || 0
                          : 0)
                    )}
                  </div>
                )}
                <div
                  style={{
                    borderTop: "2px solid #1976d2",
                    paddingTop: "8px",
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#1565c0",
                  }}
                >
                  Total:{" "}
                  {formatTotalAmount(
                    (formData.saleDeedAmount || 0) +
                      (!hiddenFields.booking.chargesAmount
                        ? formData.chargesAmount || 0
                        : 0) +
                      (!hiddenFields.booking.otherWorkAmount
                        ? formData.otherWorkAmount || 0
                        : 0) +
                      (!hiddenFields.booking.terraceAmount
                        ? formData.terraceAmount || 0
                        : 0) +
                      (!hiddenFields.booking.banakhatAmount
                        ? formData.banakhatAmount || 0
                        : 0)
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Plans */}
          <div className="form-section">
            <div className="section-header">
              <h3>
                <FaRupeeSign /> Add Payment Plans (Optional)
              </h3>
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={addPaymentPlan}
                disabled={loading}
              >
                <FaPlus /> Add Payment Plan
              </button>
            </div>

            {paymentPlans.length === 0 && (
              <p className="no-plans-message">
                No payment plans added yet. You can add payment plans for token,
                down payment, installments, loan payments, etc.
              </p>
            )}

            {paymentPlans.map((plan, index) => (
              <div key={index} className="payment-plan-section">
                <div className="plan-header">
                  <h4>Payment Plan {index + 1}</h4>
                  <button
                    type="button"
                    className="btn btn-danger btn-small"
                    onClick={() => removePaymentPlan(index)}
                    disabled={loading}
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Payment Type *</label>
                    <select
                      value={plan.planType}
                      onChange={(e) =>
                        updatePaymentPlan(index, "planType", e.target.value)
                      }
                      disabled={loading}
                    >
                      <option value="">Select Payment Type</option>
                      <option value="token">Token</option>
                      <option value="down_payment">Down Payment</option>
                      <option value="installment">Installment</option>
                      <option value="loan">Loan</option>
                      <option value="mixed">Mixed</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Amount *</label>
                    <input
                      type="number"
                      value={plan.amount === 0 ? "" : plan.amount || ""}
                      onChange={(e) =>
                        updatePaymentPlan(
                          index,
                          "amount",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      onWheel={(e) => e.currentTarget.blur()}
                      min="0"
                      disabled={loading}
                      placeholder="Enter amount"
                    />
                  </div>
                </div>

                {/* Loan specific fields */}
                {plan.planType === "loan" && (
                  <div className="loan-details">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Bank</label>
                        <input
                          type="text"
                          value={plan.bank || ""}
                          onChange={(e) =>
                            updatePaymentPlan(index, "bank", e.target.value)
                          }
                          disabled={loading}
                          placeholder="Bank name"
                        />
                      </div>

                      <div className="form-group">
                        <label>By Builder</label>
                        <select
                          value={plan.byBuilder ? "true" : "false"}
                          onChange={(e) =>
                            updatePaymentPlan(
                              index,
                              "byBuilder",
                              e.target.value === "true"
                            )
                          }
                          disabled={loading}
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      </div>
                    </div>

                    {plan.byBuilder && (
                      <div className="form-group">
                        <label>Agent Name</label>
                        <input
                          type="text"
                          value={plan.agentName || ""}
                          onChange={(e) =>
                            updatePaymentPlan(
                              index,
                              "agentName",
                              e.target.value
                            )
                          }
                          disabled={loading}
                          placeholder="Agent name"
                        />
                      </div>
                    )}

                    <div className="form-row">
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          value={plan.status || ""}
                          onChange={(e) =>
                            updatePaymentPlan(index, "status", e.target.value)
                          }
                          disabled={loading}
                        >
                          <option value="">Select Status</option>
                          <option value="in_process">In Process</option>
                          <option value="processed">Processed</option>
                          <option value="declined">Declined</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Date</label>
                        <input
                          type="date"
                          value={plan.loanDate || ""}
                          onChange={(e) =>
                            updatePaymentPlan(index, "loanDate", e.target.value)
                          }
                          max={getMaxTransactionDate()}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {plan.planType === "installment" && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Frequency</label>
                      <select
                        value={plan.frequency || ""}
                        onChange={(e) =>
                          updatePaymentPlan(index, "frequency", e.target.value)
                        }
                        disabled={loading}
                      >
                        <option value="">Select Frequency</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Duration (number of payments)</label>
                      <input
                        type="number"
                        value={plan.duration || ""}
                        onChange={(e) =>
                          updatePaymentPlan(
                            index,
                            "duration",
                            parseInt(e.target.value) || null
                          )
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        min="1"
                        disabled={loading}
                        placeholder="e.g., 12"
                      />
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Installment Date</label>
                    <input
                      type="date"
                      value={plan.startDate || ""}
                      onChange={(e) =>
                        updatePaymentPlan(index, "startDate", e.target.value)
                      }
                      max={getMaxTransactionDate()}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={plan.description || ""}
                    onChange={(e) =>
                      updatePaymentPlan(index, "description", e.target.value)
                    }
                    rows={2}
                    disabled={loading}
                    placeholder="Additional details about this payment plan..."
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/bookings")}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Booking"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBooking;
